import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

type ReportTab = 'overview' | 'daily' | 'cashier' | 'product' | 'category' | 'payment' | 'lowstock' | 'supplier' | 'profit';

const POSReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '';
      const params: any = { date_from: dateFrom, date_to: dateTo };

      switch (activeTab) {
        case 'overview': endpoint = '/pos/reports/overview'; break;
        case 'daily': endpoint = '/pos/reports/daily-sales'; break;
        case 'cashier': endpoint = '/pos/reports/sales-by-cashier'; break;
        case 'product': endpoint = '/pos/reports/sales-by-product'; break;
        case 'category': endpoint = '/pos/reports/sales-by-category'; break;
        case 'payment': endpoint = '/pos/reports/sales-by-payment'; break;
        case 'lowstock': endpoint = '/pos/reports/low-stock'; break;
        case 'supplier': endpoint = '/pos/reports/supplier-balances'; break;
        case 'profit': endpoint = '/pos/reports/profit'; break;
      }

      const response = await apiService.get(endpoint, { params });
      setData(response);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, dateFrom, dateTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const tabs: { id: ReportTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'daily', label: 'Daily Sales' },
    { id: 'cashier', label: 'By Cashier' },
    { id: 'product', label: 'By Product' },
    { id: 'category', label: 'By Category' },
    { id: 'payment', label: 'By Payment' },
    { id: 'profit', label: 'Profit & Loss' },
    { id: 'lowstock', label: 'Low Stock' },
    { id: 'supplier', label: 'Supplier Dues' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Sales, inventory, and profit analytics</p>
        </div>
        {activeTab !== 'overview' && activeTab !== 'lowstock' && activeTab !== 'supplier' && (
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
            <span className="text-gray-400">to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 px-4 overflow-x-auto">
          <nav className="flex space-x-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading report...</div>
          ) : (
            <>
              {/* Overview */}
              {activeTab === 'overview' && data && (
                <div className="space-y-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase">Today</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <StatCard label="Total Sales" value={`${Number(data.today_total_sales).toLocaleString()} MMK`} color="green" />
                    <StatCard label="POS Sales" value={`${Number(data.today_pos_sales).toLocaleString()} MMK`} />
                    <StatCard label="POS Transactions" value={data.today_pos_transactions} />
                    <StatCard label="Web Sales" value={`${Number(data.today_web_sales).toLocaleString()} MMK`} />
                    <StatCard label="Web Orders" value={data.today_web_orders} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase">This Month</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <StatCard label="Total Sales" value={`${Number(data.month_total_sales).toLocaleString()} MMK`} color="green" />
                    <StatCard label="POS Sales" value={`${Number(data.month_pos_sales).toLocaleString()} MMK`} />
                    <StatCard label="POS Transactions" value={data.month_pos_transactions} />
                    <StatCard label="Web Sales" value={`${Number(data.month_web_sales).toLocaleString()} MMK`} />
                    <StatCard label="Web Orders" value={data.month_web_orders} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase">Inventory & Suppliers</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Products" value={data.total_products} />
                    <StatCard label="Out of Stock" value={data.out_of_stock} color="red" />
                    <StatCard label="Stock Value" value={`${Number(data.stock_value).toLocaleString()} MMK`} />
                    <StatCard label="Supplier Due" value={`${Number(data.supplier_due).toLocaleString()} MMK`} color="red" />
                  </div>
                </div>
              )}

              {/* Daily Sales */}
              {activeTab === 'daily' && data && (
                <div>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <StatCard label="Total Revenue" value={`${Number(data.totals?.total_revenue || 0).toLocaleString()} MMK`} color="green" />
                    <StatCard label="Transactions" value={data.totals?.total_transactions || 0} />
                    <StatCard label="Total Discounts" value={`${Number(data.totals?.total_discounts || 0).toLocaleString()} MMK`} color="red" />
                    <StatCard label="Avg/Day" value={`${Number(data.totals?.avg_per_day || 0).toLocaleString()} MMK`} />
                  </div>
                  <ReportTable
                    headers={['Date', 'Transactions', 'Revenue (MMK)', 'Discounts (MMK)']}
                    rows={(data.days || []).map((d: any) => [d.date, d.transactions, Number(d.revenue).toLocaleString(), Number(d.discounts).toLocaleString()])}
                  />
                </div>
              )}

              {/* By Cashier */}
              {activeTab === 'cashier' && (
                <ReportTable
                  headers={['Cashier', 'Transactions', 'Revenue (MMK)']}
                  rows={(Array.isArray(data) ? data : []).map((d: any) => [d.cashier_name, d.transactions, Number(d.revenue).toLocaleString()])}
                />
              )}

              {/* By Product */}
              {activeTab === 'product' && (
                <ReportTable
                  headers={['Product', 'Qty Sold', 'Revenue (MMK)', 'Cost (MMK)', 'Profit (MMK)']}
                  rows={(Array.isArray(data) ? data : []).map((d: any) => [d.product_name, d.total_qty, Number(d.total_revenue).toLocaleString(), Number(d.total_cost).toLocaleString(), Number(d.profit).toLocaleString()])}
                />
              )}

              {/* By Category */}
              {activeTab === 'category' && (
                <ReportTable
                  headers={['Category', 'Qty Sold', 'Revenue (MMK)']}
                  rows={(Array.isArray(data) ? data : []).map((d: any) => [d.category_name, d.total_qty, Number(d.total_revenue).toLocaleString()])}
                />
              )}

              {/* By Payment */}
              {activeTab === 'payment' && (
                <ReportTable
                  headers={['Payment Method', 'Transactions', 'Revenue (MMK)']}
                  rows={(Array.isArray(data) ? data : []).map((d: any) => [d.payment_method?.replace('_', ' ').toUpperCase(), d.transactions, Number(d.revenue).toLocaleString()])}
                />
              )}

              {/* Profit */}
              {activeTab === 'profit' && data?.summary && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <StatCard label="Total Revenue" value={`${Number(data.summary.total_revenue).toLocaleString()} MMK`} color="green" />
                    <StatCard label="Total COGS" value={`${Number(data.summary.total_cogs).toLocaleString()} MMK`} color="red" />
                    <StatCard label="Gross Profit" value={`${Number(data.summary.gross_profit).toLocaleString()} MMK`} color="blue" />
                    <StatCard label="Margin" value={`${data.summary.margin_percentage}%`} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <StatCard label="POS Revenue" value={`${Number(data.summary.pos_revenue).toLocaleString()} MMK`} />
                    <StatCard label="Web Revenue" value={`${Number(data.summary.web_revenue).toLocaleString()} MMK`} />
                    <StatCard label="Total Discounts" value={`${Number(data.summary.total_discounts).toLocaleString()} MMK`} color="red" />
                    <StatCard label="Supplier Payments" value={`${Number(data.summary.supplier_payments).toLocaleString()} MMK`} />
                  </div>
                  <ReportTable
                    headers={['Date', 'POS (MMK)', 'Web (MMK)', 'Total Revenue (MMK)', 'Cost (MMK)', 'Profit (MMK)']}
                    rows={(data.daily || []).map((d: any) => [
                      d.date,
                      Number(d.pos_revenue).toLocaleString(),
                      Number(d.web_revenue).toLocaleString(),
                      Number(d.revenue).toLocaleString(),
                      Number(d.cost).toLocaleString(),
                      Number(d.profit).toLocaleString()
                    ])}
                  />
                </div>
              )}

              {/* Low Stock */}
              {activeTab === 'lowstock' && (
                <div>
                  <p className="text-sm text-gray-500 mb-4">Products with stock ≤ 10 units</p>
                  <ReportTable
                    headers={['Product', 'SKU', 'Total Stock', 'Low Items']}
                    rows={(Array.isArray(data) ? data : []).map((d: any) => [
                      d.name,
                      d.product_code,
                      d.total_stock,
                      d.low_items?.map((i: any) => `${i.variant}/${i.size}: ${i.stock}`).join(', ') || '-',
                    ])}
                  />
                </div>
              )}

              {/* Supplier Balances */}
              {activeTab === 'supplier' && (
                <ReportTable
                  headers={['Supplier', 'Company', 'Purchases', 'Total Amount (MMK)', 'Paid (MMK)', 'Due (MMK)']}
                  rows={(Array.isArray(data) ? data : []).map((d: any) => [
                    d.name,
                    d.company || '-',
                    d.total_purchases,
                    Number(d.total_amount).toLocaleString(),
                    Number(d.total_paid).toLocaleString(),
                    Number(d.total_due).toLocaleString(),
                  ])}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Reusable stat card
const StatCard: React.FC<{ label: string; value: any; color?: string }> = ({ label, value, color }) => {
  const colorClass = color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : color === 'blue' ? 'text-blue-600' : 'text-gray-900';
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
};

// Reusable table
const ReportTable: React.FC<{ headers: string[]; rows: any[][] }> = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="px-4 py-8 text-center text-gray-500">No data for selected period</td></tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {row.map((cell: any, j: number) => (
                <td key={j} className="px-4 py-3 text-sm text-gray-700">{cell}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default POSReports;
